import {sendCustomMetric} from '@github-ui/stats'

import type {ClientInteractionFact} from './client-interaction-contract'
import type {ClientRecoveryEvent, ClientRecoveryFact} from './client-recovery-contract'
import type {ResultPresentationEvent, ResultPresentationFact} from './result-presentation-contract'
import type {UiReadinessFact} from './ui-readiness-contract'

type MetricSender = (metric: Parameters<typeof sendCustomMetric>[0]) => void
type Outcome = 'success' | 'failure' | 'unresolved' | 'coverage_lost'

const MAX_ACTIVE_METRIC_UNITS = 512
const MAX_FINALIZED_METRIC_IDS = 512

function createEmitter(send: MetricSender, reportError: () => void) {
  return (metric: Parameters<MetricSender>[0]) => {
    try {
      send(metric)
    } catch {
      reportError()
    }
  }
}

function commonTags(fact: {surface: string; role: string}) {
  return {surface: fact.surface, role: fact.role}
}

function rememberFinalized(finalized: Set<string>, id: string) {
  finalized.add(id)
  if (finalized.size <= MAX_FINALIZED_METRIC_IDS) return
  const oldest = finalized.values().next().value
  if (oldest) finalized.delete(oldest)
}

function evictOldest<T>(active: Map<string, T>, finalize: (id: string, value: T) => void) {
  if (active.size <= MAX_ACTIVE_METRIC_UNITS) return
  const oldest = active.entries().next().value
  if (oldest) finalize(...oldest)
}

interface UiReadinessMetricState {
  open: UiReadinessFact
  latest: UiReadinessFact
}

export function createUiReadinessMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, UiReadinessMetricState>()
  const finalized = new Set<string>()

  const outcome = (fact: UiReadinessFact, value: Outcome) => {
    const id = fact.client_open_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit({
      name: 'ONE_COPILOT_WEB_CLIENT_READINESS_OUTCOME',
      value: 1,
      tags: {...commonTags(fact), entry_kind: fact.entry_kind, outcome: value},
    })
  }
  const unresolved = (id: string, state: UiReadinessMetricState) => outcome(state.latest, 'unresolved')

  return {
    observe(fact: UiReadinessFact) {
      const id = fact.client_open_id
      if (finalized.has(id)) return
      if (fact.observation === 'open') {
        active.set(id, {open: fact, latest: fact})
        evictOldest(active, unresolved)
      } else {
        const state = active.get(id)
        if (state) state.latest = fact
        if (fact.observation === 'input_ready') {
          outcome(fact, 'success')
          const start =
            fact.origin_clock_id === fact.clock_id && fact.origin_monotonic_ms !== undefined
              ? fact.origin_monotonic_ms
              : state?.open.clock_id === fact.clock_id
                ? state.open.monotonic_ms
                : undefined
          if (start !== undefined && fact.monotonic_ms >= start) {
            emit({
              name: 'ONE_COPILOT_WEB_CLIENT_READINESS_LATENCY',
              value: fact.monotonic_ms - start,
              tags: {...commonTags(fact), entry_kind: fact.entry_kind},
            })
          }
        } else if (fact.observation === 'failure') {
          outcome(fact, 'failure')
        } else if (
          fact.observation === 'page_exit' ||
          (fact.observation === 'view_disposed' && fact.reason === 'closed')
        ) {
          outcome(fact, 'unresolved')
        }
      }
    },
  }
}

interface ClientInteractionMetricState {
  latest: ClientInteractionFact
}

export function createClientInteractionMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ClientInteractionMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ClientInteractionFact) => ({
    ...commonTags(fact),
    interaction_kind: fact.interaction_kind,
    handoff_required:
      fact.handoff_scope === 'single_action' || fact.handoff_scope === 'send_now_and_remove_queued' ? 'true' : 'false',
  })
  const outcome = (fact: ClientInteractionFact, value: Outcome) => {
    const id = fact.client_interaction_id
    if (finalized.has(id)) return false
    active.delete(id)
    rememberFinalized(finalized, id)
    emit({
      name: 'ONE_COPILOT_WEB_CLIENT_INTERACTION_OUTCOME',
      value: 1,
      tags: {...tags(fact), outcome: value},
    })
    return true
  }
  const unresolved = (_id: string, state: ClientInteractionMetricState) => outcome(state.latest, 'unresolved')

  return {
    observe(fact: ClientInteractionFact) {
      const id = fact.client_interaction_id
      if (finalized.has(id)) return
      if (fact.observation === 'committed') {
        active.set(id, {latest: fact})
        evictOldest(active, unresolved)
      } else {
        const state = active.get(id)
        if (state) state.latest = fact
        if (fact.observation === 'failed') {
          outcome(fact, 'failure')
        } else if (fact.observation === 'completed' && outcome(fact, 'success')) {
          if (fact.monotonic_ms >= fact.input_start_ms) {
            emit({
              name: 'ONE_COPILOT_WEB_CLIENT_INTERACTION_LATENCY',
              value: fact.monotonic_ms - fact.input_start_ms,
              tags: tags(fact),
            })
          }
        }
      }
    },
    pageExit() {
      for (const [id, state] of active) unresolved(id, state)
      active.clear()
    },
  }
}

interface ResultPresentationMetricState {
  latest: ResultPresentationFact
  completion?: ResultPresentationFact
  failure?: ResultPresentationFact
}

export function createResultPresentationMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ResultPresentationMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ResultPresentationFact) => ({
    ...commonTags(fact),
    receipt_kind: fact.milestone_kind,
  })
  const outcome = (fact: ResultPresentationFact, value: Outcome) => {
    const id = fact.receipt_event_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit({
      name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_OUTCOME',
      value: 1,
      tags: {...tags(fact), outcome: value},
    })
  }
  const finalize = (_id: string, state: ResultPresentationMetricState) => {
    const {completion, failure, latest} = state
    if (
      completion?.observation === 'completed' &&
      completion &&
      !completion.visibility_interrupted &&
      (failure?.observation !== 'failed' || failure.monotonic_ms > completion.closure_time_ms)
    ) {
      outcome(completion, 'success')
      if (completion.closure_time_ms >= completion.receipt_monotonic_ms) {
        emit({
          name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_LATENCY',
          value: completion.closure_time_ms - completion.receipt_monotonic_ms,
          tags: tags(completion),
        })
      }
    } else if (
      failure?.observation === 'failed' &&
      (completion?.observation !== 'completed' || failure.monotonic_ms <= completion.closure_time_ms)
    ) {
      outcome(failure, 'failure')
    } else {
      outcome(latest, 'unresolved')
    }
  }

  return {
    observe(fact: ResultPresentationEvent) {
      if (fact.observation === 'coverage_lost') {
        emit({
          name: 'ONE_COPILOT_WEB_RESULT_PRESENTATION_OUTCOME',
          value: 1,
          tags: {...commonTags(fact), outcome: 'coverage_lost'},
        })
        return
      }
      const id = fact.receipt_event_id
      if (finalized.has(id)) return
      const state = active.get(id)
      if (fact.observation === 'receipt') {
        active.set(id, {latest: fact})
        evictOldest(active, finalize)
      } else if (state) {
        state.latest = fact
        if (fact.observation === 'completed') state.completion = fact
        else if (fact.observation === 'failed' && (!state.failure || fact.monotonic_ms < state.failure.monotonic_ms))
          state.failure = fact
      }
    },
    dispose() {
      for (const entry of active) finalize(...entry)
      active.clear()
    },
  }
}

interface ClientRecoveryMetricState {
  latest: ClientRecoveryFact
  completion?: ClientRecoveryFact
  failure?: ClientRecoveryFact
}

export function createClientRecoveryMetricObserver(
  send: MetricSender = sendCustomMetric,
  reportError: () => void = () => undefined,
) {
  const emit = createEmitter(send, reportError)
  const active = new Map<string, ClientRecoveryMetricState>()
  const finalized = new Set<string>()
  const tags = (fact: ClientRecoveryFact) => ({
    ...commonTags(fact),
    recovery_path: fact.recovery_path,
    trigger: fact.trigger,
  })
  const outcome = (fact: ClientRecoveryFact, value: Outcome) => {
    const id = fact.episode_id
    if (finalized.has(id)) return
    active.delete(id)
    rememberFinalized(finalized, id)
    emit({
      name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_OUTCOME',
      value: 1,
      tags: {...tags(fact), outcome: value},
    })
  }
  const finalize = (_id: string, state: ClientRecoveryMetricState) => {
    const {completion, failure, latest} = state
    if (
      completion?.observation === 'completed' &&
      (failure?.observation !== 'failed' || failure.monotonic_ms > completion.closure_time_ms)
    ) {
      outcome(completion, 'success')
      if (completion.closure_time_ms >= completion.episode_monotonic_ms) {
        emit({
          name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_LATENCY',
          value: completion.closure_time_ms - completion.episode_monotonic_ms,
          tags: tags(completion),
        })
      }
    } else if (failure?.observation === 'failed') {
      outcome(failure, 'failure')
    } else {
      outcome(latest, 'unresolved')
    }
  }

  return {
    observe(fact: ClientRecoveryEvent) {
      if (fact.observation === 'coverage_lost') {
        emit({
          name: 'ONE_COPILOT_WEB_CLIENT_RECOVERY_OUTCOME',
          value: 1,
          tags: {...commonTags(fact), outcome: 'coverage_lost'},
        })
        return
      }
      const id = fact.episode_id
      if (finalized.has(id)) return
      const state = active.get(id)
      if (fact.observation === 'episode_started') {
        active.set(id, {latest: fact})
        evictOldest(active, finalize)
      } else if (state) {
        state.latest = fact
        if (fact.observation === 'completed') state.completion = fact
        else if (fact.observation === 'failed' && (!state.failure || fact.monotonic_ms < state.failure.monotonic_ms))
          state.failure = fact
      }
    },
    dispose() {
      for (const entry of active) finalize(...entry)
      active.clear()
    },
  }
}
